import React from 'react';
import styled, { css } from 'styled-components';
import theme from '~/styling/theme';
import Text from '~/components/interface/Text';

const Wrapper = styled.div`
  position: relative;
  overflow: hidden;

  ${({ lazyload }) =>
    lazyload &&
    css`
      img.ls-blur-up-is-loading,
      img.lazyload:not([src]) {
        visibility: hidden;
      }

      .ls-blur-up-img {
        position: absolute;
        left: 0;
        top: 0;
        filter: blur(15px);
        opacity: 1;
        transition: opacity 1000ms, filter 1500ms;
      }

      .ls-blur-up-img.ls-inview.ls-original-loaded {
        opacity: 0;
        filter: blur(5px);
      }
    `}
  .ls-blur-up-img,
  img {
    width: 100%;
    max-width: 100%;
    height: 100%;
    font-family: 'blur-up: auto', 'object-fit: cover';
    object-fit: cover;
    aspect-ratio: var(--mobile-ratio-width) / var(--mobile-ratio-height);

    @media (${theme.respondTo.tablet}) {
      aspect-ratio: var(--tablet-ratio-width) / var(--tablet-ratio-height);
    }

    @media (${theme.respondTo.desktop}) {
      aspect-ratio: var(--desktop-ratio-width) / var(--desktop-ratio-height);
    }
  }
`;

const StyledText = styled(Text)`
  opacity: 30%;
  padding-top: 0.8rem;
`;

const Image = ({ image, lazyload = true, withCredits = false }) => {
  if (!image) return null;
  const {
    placeholder,
    mobile,
    mobile2x,
    tablet,
    tablet2x,
    desktop,
    desktop2x,
    desktopL,
    desktopL2x,
    desktopXL,
    desktopXXL,
    mobileWebp,
    mobile2xWebp,
    tabletWebp,
    tablet2xWebp,
    desktopWebp,
    desktop2xWebp,
    desktopLWebp,
    desktopL2xWebp,
    desktopXLWebp,
    desktopXXLWebp,
    title,
    mimeType,
    altText,
    credit,
  } = image;

  const ratioStyles = {
    '--mobile-ratio-width': image.mobileRatioWidth,
    '--mobile-ratio-height': image.mobileRatioHeight,
    '--tablet-ratio-width': image.tabletRatioWidth,
    '--tablet-ratio-height': image.tabletRatioHeight,
    '--desktop-ratio-width': image.desktopRatioWidth,
    '--desktop-ratio-height': image.desktopRatioHeight,
  };

  return (
    <Wrapper lazyload={lazyload} className="image-wrapper">
      <picture>
        {desktopXXL && lazyload && (
          <source data-srcset={`${desktopXXLWebp} 1x`} media={`(${theme.respondTo.desktopXXL})`} type="image/webp" />
        )}

        {desktopXXL && !lazyload && (
          <source srcSet={`${desktopXXLWebp} 1x`} media={`(${theme.respondTo.desktopXXL})`} type="image/webp" />
        )}

        {desktopXL && lazyload && (
          <source data-srcset={`${desktopXLWebp} 1x`} media={`(${theme.respondTo.desktopXL})`} type="image/webp" />
        )}

        {desktopXL && !lazyload && (
          <source srcSet={`${desktopXLWebp} 1x`} media={`(${theme.respondTo.desktopXL})`} type="image/webp" />
        )}

        {desktopL && lazyload && (
          <source
            data-srcset={`${desktopLWebp} 1x, ${desktopL2xWebp} 2x`}
            media={`(${theme.respondTo.desktopL})`}
            type="image/webp"
          />
        )}

        {desktopL && !lazyload && (
          <source
            srcSet={`${desktopLWebp} 1x, ${desktopL2xWebp} 2x`}
            media={`(${theme.respondTo.desktopL})`}
            type="image/webp"
          />
        )}

        {desktop && lazyload && (
          <source
            data-srcset={`${desktopWebp} 1x, ${desktop2xWebp} 2x`}
            media={`(${theme.respondTo.desktop})`}
            type="image/webp"
          />
        )}

        {desktop && !lazyload && (
          <source
            srcSet={`${desktopWebp} 1x, ${desktop2xWebp} 2x`}
            media={`(${theme.respondTo.desktop})`}
            type="image/webp"
          />
        )}

        {tablet && lazyload && (
          <source
            data-srcset={`${tabletWebp} 1x, ${tablet2xWebp} 2x`}
            media={`(${theme.respondTo.tablet})`}
            type="image/webp"
          />
        )}

        {tablet && !lazyload && (
          <source
            srcSet={`${tabletWebp} 1x, ${tablet2xWebp} 2x`}
            media={`(${theme.respondTo.tablet})`}
            type="image/webp"
          />
        )}

        {lazyload && (
          <source
            data-srcset={`${mobileWebp} 1x, ${mobile2xWebp} 2x`}
            media={`(${theme.respondTo.mobile})`}
            type="image/webp"
          />
        )}

        {!lazyload && (
          <source
            srcSet={`${mobileWebp} 1x, ${mobile2xWebp} 2x`}
            media={`(${theme.respondTo.mobile})`}
            type="image/webp"
          />
        )}

        {desktopXXL && lazyload && (
          <source data-srcset={`${desktopXXL} 1x`} media={`(${theme.respondTo.desktopXXL})`} type={mimeType} />
        )}

        {desktopXXL && !lazyload && (
          <source srcSet={`${desktopXXL} 1x`} media={`(${theme.respondTo.desktopXXL})`} type={mimeType} />
        )}

        {desktopXL && lazyload && (
          <source data-srcset={`${desktopXL} 1x`} media={`(${theme.respondTo.desktopXL})`} type={mimeType} />
        )}

        {desktopXL && !lazyload && (
          <source srcSet={`${desktopXL} 1x`} media={`(${theme.respondTo.desktopXL})`} type={mimeType} />
        )}

        {desktopL && lazyload && (
          <source
            data-srcset={`${desktopL} 1x, ${desktopL2x} 2x`}
            media={`(${theme.respondTo.desktopL})`}
            type={mimeType}
          />
        )}

        {desktopL && !lazyload && (
          <source srcSet={`${desktopL} 1x, ${desktopL2x} 2x`} media={`(${theme.respondTo.desktopL})`} type={mimeType} />
        )}

        {desktop && lazyload && (
          <source
            data-srcset={`${desktop} 1x, ${desktop2x} 2x`}
            media={`(${theme.respondTo.desktop})`}
            type={mimeType}
          />
        )}

        {desktop && !lazyload && (
          <source srcSet={`${desktop} 1x, ${desktop2x} 2x`} media={`(${theme.respondTo.desktop})`} type={mimeType} />
        )}

        {tablet && lazyload && (
          <source data-srcset={`${tablet} 1x, ${tablet2x} 2x`} media={`(${theme.respondTo.tablet})`} type={mimeType} />
        )}

        {tablet && !lazyload && (
          <source srcSet={`${tablet} 1x, ${tablet2x} 2x`} media={`(${theme.respondTo.tablet})`} type={mimeType} />
        )}

        {lazyload && (
          <source data-srcset={`${mobile} 1x, ${mobile2x} 2x`} media={`(${theme.respondTo.mobile})`} type={mimeType} />
        )}

        {!lazyload && (
          <source srcSet={`${mobile} 1x, ${mobile2x} 2x`} media={`(${theme.respondTo.mobile})`} type={mimeType} />
        )}

        {lazyload ? (
          <img
            data-src={mobile}
            data-sizes="auto"
            data-lowsrc={placeholder}
            className="lazyload"
            alt={altText || title}
            style={ratioStyles}
          />
        ) : (
          <img src={mobile} alt={altText || title} style={ratioStyles} />
        )}
      </picture>
      {withCredits && credit && (
        <span>
          <StyledText size="xxs">{credit}</StyledText>
        </span>
      )}
    </Wrapper>
  );
};

export default Image;
